import { useEffect, useRef, useState } from "react";
import Waveform from "../ButtonsAndControls/VideoAndAudioOnly/Waveform";
import AudioPlayerTimeDisplay, {
  PlayerTimeState,
} from "../ButtonsAndControls/VideoAndAudioOnly/TimeDisplay";
import AudioControls from "./AudioControls";
import { getDefaultVolume } from "../MediaDrawer";
import LoadingIndicator from "../../common/LoadingIndicator/LoadingIndicator";
import { PLAYBACK_SPEED_OPTION } from "../Video/VideoPlayer";

export interface AudioPlayerProps {
  audioPath: string | undefined;
  isPlaying: boolean;
  isLoading: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  playerInteractionId: string | undefined;
  handleOpenInNewWindow?: (playerInteractionId: string) => void;
  redactionTimestamps?: { start: string; end: string }[];
}

const AudioPlayer = ({
  audioPath,
  isPlaying,
  setIsPlaying,
  playerInteractionId,
  isLoading,
  handleOpenInNewWindow,
  redactionTimestamps,
}: AudioPlayerProps) => {
  const [timeState, setTimeState] = useState<PlayerTimeState | undefined>();
  const [muted, setMuted] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(getDefaultVolume(50));
  const [playbackSpeed, setPlaybackSpeed] = useState<PLAYBACK_SPEED_OPTION>(1);
  const [isWaveformLoading, setIsWaveformLoading] = useState<boolean>(false);
  const isWaveLoading = isWaveformLoading || isLoading;
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);

  const audioRef = useRef<HTMLMediaElement | null>(null);
  useEffect(() => {
    const reset = () => {
      setFailedToLoad(false);
    };

    reset();
  }, [audioPath]);

  if (failedToLoad) {
    return <>Failed to load audio.</>;
  }

  return (
    <div>
      {isWaveLoading && (
        <div className="w-full flex justify-center">
          <LoadingIndicator />
        </div>
      )}
      <audio
        key={`${playerInteractionId}-audio`}
        ref={audioRef}
        preload="auto"
        style={{ display: "none" }}
      />
      <Waveform
        key={`${playerInteractionId}-waveform`}
        className="mb-2"
        height={80}
        audioPath={audioPath}
        setIsLoading={setIsWaveformLoading}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        volume={volume}
        muted={muted}
        setFailedToLoad={setFailedToLoad}
        onTimeChange={setTimeState}
        redactionTimestamps={redactionTimestamps}
        playbackSpeed={playbackSpeed}
        mediaElement={audioRef}
      />
      <div className="flex items-center text-center">
        <AudioPlayerTimeDisplay timeState={timeState} />
      </div>
      <AudioControls
        NewWindowButtonProps={{
          handleOpenInNewWindow,
          playerInteractionId,
          isLoading,
        }}
        PlayButtonProps={{
          isPlaying,
          setIsPlaying,
          disabled: isWaveLoading,
        }}
        VolumeControlProps={{
          muted,
          setMuted,
          volume,
          setVolume: setVolume,
          isLoading,
        }}
        PlaybackControlProps={{
          playbackSpeed,
          setPlaybackSpeed,
        }}
      />
    </div>
  );
};

export default AudioPlayer;
